import {useId} from 'react';
import {
  Box,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from '@wellstone-solutions/web';
import {RequiredMark, visuallyHiddenSx} from '../NoteForm';
import type {Checklist, Choice} from '../types';
import {OTHER} from '../types';

type ChecklistSectionProps = {
  label: string;
  options: readonly Choice[];
  value: Checklist;
  onToggle: (key: string) => void;
  onOtherChange: (text: string) => void;
  otherLabel?: string;
  required?: boolean;
};

export const ChecklistSection = ({
  label,
  options,
  value,
  onToggle,
  onOtherChange,
  otherLabel = 'Other (specify)',
  required = false,
}: ChecklistSectionProps) => {
  const labelId = useId();
  const hintId = useId();

  return (
    <Stack spacing={1}>
      <Box
        role="group"
        aria-labelledby={labelId}
        aria-describedby={required ? hintId : undefined}>
        <Typography id={labelId} variant="subtitle1" mb={0.5}>
          {label}
          {required && <RequiredMark />}
        </Typography>
        {/* The asterisk is hidden from screen readers, and a group can't take aria-required */}
        {required && (
          <Box id={hintId} sx={visuallyHiddenSx}>
            Select at least one.
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            '& .MuiFormControlLabel-root': {
              width: {xs: '100%', md: '50%'},
              alignItems: 'flex-start',
              my: 0.5,
              mr: 0,
              // Keeps a long left-column label off the right column's checkbox
              pr: 2,
              boxSizing: 'border-box',
            },
            // Lines the box up with a wrapped label's first line; less padding would skew the hover circle
            '& .MuiCheckbox-root': {mt: '-9px'},
          }}>
          {options.map((option) => (
            <Checkbox
              key={option.key}
              label={option.label}
              checked={value.selected.includes(option.key)}
              onChange={() => onToggle(option.key)}
            />
          ))}
        </Box>
      </Box>
      {value.selected.includes(OTHER) && (
        <TextField
          required
          label={otherLabel}
          value={value.other}
          onChange={(event) => onOtherChange(event.target.value)}
        />
      )}
    </Stack>
  );
};
